// Avatar — the single shared image-with-fallback used for every player /
// agent / coach photo across the app.
//
// Batch-3 C2 (QA finding N1): missing/broken photos fell back six
// different ways across surfaces — the browser's broken-image glyph
// (no onError), a position letter, an "initials" tile (two different
// colour treatments), a "No photo" text box, and an empty grey square.
// This component collapses all of that into one treatment.
//
// Behavior:
//   - A real photo renders as an <img> with a descriptive alt (the
//     person's name) — preserves the Batch-2 a11y contract ("image alt").
//   - A null/empty src OR a runtime load error (onError) swaps to a
//     decorative initials tile. The tile is aria-hidden so screen readers
//     skip it; the name is always present in adjacent text, so nothing is
//     lost (C2 brief: "placeholders skipped by SR").
//
// Sizing, shape, and border are caller-controlled via `className`, applied
// to BOTH the <img> and the fallback tile so a broken-image swap never
// changes the avatar's footprint. `textClassName` scales the initials to
// the avatar size.

import { useEffect, useState } from 'react'

export function initialsFor(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? '')
      .join('') || '?'
  )
}

interface Props {
  src: string | null | undefined
  // Used both as the <img> alt (when a photo loads) and to derive the
  // fallback initials (when it doesn't).
  name: string
  // Size + shape + optional border utilities. Applied to the img and the
  // fallback tile identically.
  className?: string
  // Initials font-size utility for this avatar size, e.g. 'text-3xl'.
  textClassName?: string
}

export default function Avatar({
  src,
  name,
  className = '',
  textClassName = 'text-sm',
}: Props) {
  const [failed, setFailed] = useState(false)

  // Reset the failure flag whenever the src changes so a component reused
  // across navigation (e.g. the profile hero moving between players) gives
  // each new photo its own load attempt instead of inheriting a prior 404.
  useEffect(() => {
    setFailed(false)
  }, [src])

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name}
        onError={() => setFailed(true)}
        className={`${className} object-cover`}
      />
    )
  }

  return (
    <div
      aria-hidden="true"
      className={`${className} bg-hoop-accent-light text-hoop-accent font-semibold flex items-center justify-center select-none`}
    >
      <span className={textClassName}>{initialsFor(name)}</span>
    </div>
  )
}
